import { setAccessToken, setAuthErrorInterceptor } from '$api/client';
import type { OperatorCredentials } from '$types/auth';
import { base } from '$app/paths';

const CREDENTIALS_KEY = 'operatorCredentials';
const OPERATOR_NAME_KEY = 'operatorGivenName';

// Operator sessions end after this much inactivity. The backend applies its own sliding
// idle TTL on the Redis token; this client timer just logs out proactively so an unattended
// console doesn't keep a privileged session open until the next request happens to 401.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

function createAuthStore() {
	let credentials = $state<OperatorCredentials | null>(null);
	let idleTimer: ReturnType<typeof setTimeout> | null = null;
	let activityBound = false;

	function syncApiClient(): void {
		setAccessToken(credentials?.accessToken ?? null);
	}

	function resetIdleTimer(): void {
		if (idleTimer) {
			clearTimeout(idleTimer);
			idleTimer = null;
		}
		if (credentials) {
			idleTimer = setTimeout(() => store.logout(), IDLE_TIMEOUT_MS);
		}
	}

	function bindActivityListeners(): void {
		if (activityBound) {
			return;
		}
		activityBound = true;
		for (const event of ACTIVITY_EVENTS) {
			window.addEventListener(event, resetIdleTimer, { passive: true });
		}
	}

	const store = {
		get isAuthenticated(): boolean {
			return credentials !== null;
		},

		get operatorId(): number | null {
			return credentials?.operatorId ?? null;
		},

		get accessToken(): string | null {
			return credentials?.accessToken ?? null;
		},

		/** Initialize auth state from localStorage on app startup. Call once in the root layout. */
		init(): void {
			const stored = localStorage.getItem(CREDENTIALS_KEY);
			if (stored) {
				try {
					credentials = JSON.parse(stored) as OperatorCredentials;
					syncApiClient();
				} catch {
					localStorage.removeItem(CREDENTIALS_KEY);
				}
			}

			setAuthErrorInterceptor(() => {
				store.logout();
			});

			bindActivityListeners();
			resetIdleTimer();
		},

		/** Persist credentials after a successful login */
		login(operatorCredentials: OperatorCredentials): void {
			credentials = operatorCredentials;
			localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(operatorCredentials));
			syncApiClient();
			resetIdleTimer();
		},

		/** Clear all auth state (explicit logout, 401, or idle timeout) */
		logout(): void {
			if (idleTimer) {
				clearTimeout(idleTimer);
				idleTimer = null;
			}
			credentials = null;
			localStorage.removeItem(CREDENTIALS_KEY);
			syncApiClient();
			// Hard reload wipes every module-level singleton, so no operator state leaks into
			// the next session on a shared console.
			window.location.assign(`${base}/login`);
		},

		/** Get the last-used operator name (for pre-filling the login form) */
		getOperatorName(): string | null {
			return localStorage.getItem(OPERATOR_NAME_KEY);
		},

		/** Save the operator name for UX convenience */
		setOperatorName(name: string): void {
			localStorage.setItem(OPERATOR_NAME_KEY, name);
		}
	};

	return store;
}

export const authStore = createAuthStore();
